import React, { useState } from 'react';
import { DB } from '../data/database';
import { useLanguage } from '../translations/LanguageContext';

/* ── helpers ── */
const genId = () => 'INQ-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
const timeAgo = (d) => {
  if (!d) return '';
  const ms = Date.now() - new Date(d).getTime();
  if (ms < 60000) return 'just now';
  if (ms < 3600000) return Math.floor(ms / 60000) + ' min';
  if (ms < 86400000) return Math.floor(ms / 3600000) + 'h';
  return Math.floor(ms / 86400000) + 'd';
};
const initials = (name) => (name || '??').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
const AVATAR_COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#EF4444', '#6366F1', '#14B8A6'];
const avatarColor = (name) => AVATAR_COLORS[Math.abs([...(name || '')].reduce((a, c) => a + c.charCodeAt(0), 0)) % AVATAR_COLORS.length];
const FLAG = { KZ: '🇰🇿', AE: '🇦🇪', RU: '🇷🇺', GE: '🇬🇪', KE: '🇰🇪', PK: '🇵🇰', US: '🇺🇸', GB: '🇬🇧', JP: '🇯🇵', IN: '🇮🇳', NG: '🇳🇬', TZ: '🇹🇿', UG: '🇺🇬', SA: '🇸🇦', KW: '🇰🇼', QA: '🇶🇦', BH: '🇧🇭', OM: '🇴🇲', TR: '🇹🇷', AZ: '🇦🇿', CN: '🇨🇳', HK: '🇭🇰', BD: '🇧🇩', CD: '🇨🇩', MZ: '🇲🇿' };
const getFlag = (cc) => FLAG[(cc || '').toUpperCase()] || '🌍';

const COLUMNS = [
  { id: 'new', label: 'New', color: '#EF4444', dot: '🔴' },
  { id: 'in_progress', label: 'In Progress', color: '#3B82F6', dot: '🔵' },
  { id: 'quoted', label: 'Quoted', color: '#F59E0B', dot: '🟡' },
  { id: 'closed', label: 'Closed', color: '#6B7280', dot: '⚫' },
];

const CHANNELS = { whatsapp: { label: 'WHATSAPP', bg: '#DCFCE7', color: '#16A34A' }, website_form: { label: 'FORM', bg: '#F3F4F6', color: '#6B7280' }, phone: { label: 'PHONE', bg: '#DBEAFE', color: '#2563EB' }, email: { label: 'EMAIL', bg: '#F3E8FF', color: '#7C3AED' }, walk_in: { label: 'WALK-IN', bg: '#FEF3C7', color: '#D97706' }, other: { label: 'OTHER', bg: '#F3F4F6', color: '#6B7280' } };

const OUTCOMES = ['sold', 'not_interested', 'wrong_number', 'competitor', 'no_response', 'other'];

function Inquiries({ user }) {
  const { t } = useLanguage();
  const [view, setView] = useState('kanban');
  const [channelFilter, setChannelFilter] = useState('');
  const [detail, setDetail] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [, forceUpdate] = useState();

  const inquiries = DB.get('inquiries') || [];
  const users = DB.get('users') || [];
  const customers = DB.get('customers') || [];

  const filtered = channelFilter ? inquiries.filter(i => i.channel === channelFilter) : inquiries;

  const save = (list) => { DB.set('inquiries', list); forceUpdate({}); };

  const updateInquiry = (id, updates) => {
    save(inquiries.map(i => i.id === id ? { ...i, ...updates, updated_at: new Date().toISOString() } : i));
    if (detail?.id === id) setDetail(prev => ({ ...prev, ...updates }));
  };

  /* ── Drag and drop ── */
  const handleDrop = (newStatus, e) => {
    e.preventDefault();
    if (!dragId) return;
    const inq = inquiries.find(i => i.id === dragId);
    if (!inq || inq.status === newStatus) { setDragId(null); return; }

    const updates = { status: newStatus };
    if (newStatus === 'in_progress' && !inq.first_response_at) {
      updates.first_response_at = new Date().toISOString();
      updates.response_time_minutes = Math.round((Date.now() - new Date(inq.created_at).getTime()) / 60000);
    }
    if (newStatus === 'quoted') {
      const amt = prompt('Quote amount (optional):');
      if (amt) { updates.quoted_amount = parseFloat(amt); updates.quoted_currency = 'USD'; }
    }
    if (newStatus === 'closed') {
      const outcome = prompt('Outcome:\n1. sold\n2. not_interested\n3. wrong_number\n4. competitor\n5. no_response\n6. other\n\nEnter number or text:');
      const map = { '1': 'sold', '2': 'not_interested', '3': 'wrong_number', '4': 'competitor', '5': 'no_response', '6': 'other' };
      updates.outcome = map[outcome] || outcome || 'other';
    }
    updateInquiry(dragId, updates);
    setDragId(null);
  };

  /* ══════════ KANBAN CARD ══════════ */
  const InquiryCard = ({ inq }) => {
    const ch = CHANNELS[inq.channel] || CHANNELS.other;
    const isHot = inq.priority === 'hot';
    const responded = (inq.messages || []).some(m => m.from === 'staff');
    const statusDot = inq.status === 'new' && !responded ? '🔴' : responded ? '🟢' : '🟡';

    return (
      <div
        draggable
        onDragStart={() => setDragId(inq.id)}
        onDragEnd={() => setDragId(null)}
        onClick={() => setDetail(inq)}
        className="card"
        style={{ padding: 14, marginBottom: 8, cursor: 'grab', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg2)', transition: 'box-shadow 0.15s' }}
        onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
      >
        {/* Header: avatar + name + time */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: avatarColor(inq.customer_name), display: 'grid', placeItems: 'center', color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
            {initials(inq.customer_name)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {inq.customer_name}
              {isHot && <span style={{ color: '#EF4444', marginLeft: 4 }}>●</span>}
            </div>
            <div style={{ fontSize: 10, color: 'var(--text3)' }}>
              {getFlag(inq.customer_country_code)} {inq.customer_country_code || ''} · {timeAgo(inq.created_at)}
            </div>
          </div>
        </div>
        {/* Subject */}
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)', marginBottom: 4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {inq.subject}
        </div>
        {/* Message preview */}
        {inq.message && (
          <div style={{ fontSize: 11, color: 'var(--text3)', fontStyle: 'italic', marginBottom: 10, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            "{inq.message.slice(0, 120)}"
          </div>
        )}
        {/* Footer: channel + msg count + status */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ padding: '2px 8px', borderRadius: 4, background: ch.bg, color: ch.color, fontSize: 9, fontWeight: 700, letterSpacing: '0.04em' }}>
            {inq.channel === 'whatsapp' ? '💬' : inq.channel === 'phone' ? '📞' : inq.channel === 'email' ? '📧' : '📋'} {ch.label}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10, color: 'var(--text3)' }}>{inq.message_count || 1} msg</span>
            <span style={{ fontSize: 12 }}>{statusDot}</span>
          </div>
        </div>
      </div>
    );
  };

  /* ══════════ NEW INQUIRY MODAL ══════════ */
  const NewInquiryModal = () => {
    const [f, setF] = useState({ customer_name: '', customer_phone: '', customer_email: '', customer_country_code: '', channel: 'whatsapp', subject: '', message: '', unit_sku: '', priority: 'normal', assigned_to: '' });
    const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

    // Auto-match existing customer by phone
    const matchedCustomer = f.customer_phone ? customers.find(c => c.phone === f.customer_phone || c.mobile === f.customer_phone) : null;

    const handleCreate = () => {
      if (!f.customer_name.trim() || !f.subject.trim()) { alert('Name and subject required.'); return; }
      // Try to find linked unit
      let unitInfo = {};
      if (f.unit_sku) {
        const allUnits = [...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])];
        const found = allUnits.find(u => u.sku === f.unit_sku);
        if (found) {
          unitInfo = { unit_id: found.id, unit_sku: found.sku, unit_name: found.make ? `${found.make} ${found.model}` : found.engineName || found.partName || '' };
        }
      }
      const newInq = {
        id: genId(),
        customer_name: f.customer_name.trim(),
        customer_phone: f.customer_phone,
        customer_email: f.customer_email,
        customer_country_code: f.customer_country_code,
        customer_id: matchedCustomer?.id || null,
        subject: f.subject.trim(),
        message: f.message,
        channel: f.channel,
        status: 'new',
        priority: f.priority,
        assigned_to: f.assigned_to || null,
        assigned_to_name: f.assigned_to ? (users.find(u => u.id === f.assigned_to)?.name || '') : '',
        messages: f.message ? [{ id: Date.now().toString(36), from: 'customer', text: f.message, channel: f.channel, timestamp: new Date().toISOString() }] : [],
        message_count: f.message ? 1 : 0,
        ...unitInfo,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      save([...inquiries, newInq]);
      setShowNew(false);
    };

    const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text1)', fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' };

    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setShowNew(false)}>
        <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 560, background: 'var(--bg1)', borderRadius: 16, border: '1px solid var(--border)', maxHeight: '90vh', overflowY: 'auto' }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text1)' }}>New Inquiry</div>
            <button onClick={() => setShowNew(false)} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 20, cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>Customer Name *</label>
                <input value={f.customer_name} onChange={e => set('customer_name', e.target.value)} style={inputStyle} placeholder="Full name" />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>Phone</label>
                <input value={f.customer_phone} onChange={e => set('customer_phone', e.target.value)} style={inputStyle} placeholder="+971 50 xxx xxxx" />
                {matchedCustomer && <div style={{ fontSize: 10, color: 'var(--green)', marginTop: 4 }}>✅ Found: {matchedCustomer.name}</div>}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>Email</label>
                <input value={f.customer_email} onChange={e => set('customer_email', e.target.value)} style={inputStyle} placeholder="email@example.com" />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>Country Code</label>
                <input value={f.customer_country_code} onChange={e => set('customer_country_code', e.target.value)} style={inputStyle} placeholder="AE, KZ, RU..." />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>Channel</label>
                <select value={f.channel} onChange={e => set('channel', e.target.value)} style={inputStyle}>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="phone">Phone</option>
                  <option value="email">Email</option>
                  <option value="website_form">Website Form</option>
                  <option value="walk_in">Walk-in</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>Priority</label>
                <select value={f.priority} onChange={e => set('priority', e.target.value)} style={inputStyle}>
                  <option value="normal">Normal</option>
                  <option value="hot">🔴 Hot</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>Assign To</label>
                <select value={f.assigned_to} onChange={e => set('assigned_to', e.target.value)} style={inputStyle}>
                  <option value="">Unassigned</option>
                  {users.filter(u => u.role === 'admin' || u.role === 'head_admin').map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>Subject *</label>
              <input value={f.subject} onChange={e => set('subject', e.target.value)} style={inputStyle} placeholder="What are they asking about?" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>Message</label>
              <textarea value={f.message} onChange={e => set('message', e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Customer's message..." />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>Link to Unit (SKU)</label>
              <input value={f.unit_sku} onChange={e => set('unit_sku', e.target.value)} style={inputStyle} placeholder="e.g. VEH-TOY-001" />
            </div>
            <button onClick={handleCreate} className="btn btn-primary" style={{ marginTop: 4 }}>Create Inquiry</button>
          </div>
        </div>
      </div>
    );
  };

  /* ══════════ DETAIL PANEL ══════════ */
  const DetailPanel = () => {
    const [reply, setReply] = useState('');
    const [showConvert, setShowConvert] = useState(false);
    const inq = detail;
    if (!inq) return null;

    const ch = CHANNELS[inq.channel] || CHANNELS.other;
    const messages = inq.messages || [];
    const waNum = (inq.customer_phone || '').replace(/[^\d]/g, '');

    const sendReply = () => {
      if (!reply.trim()) return;
      const msg = { id: Date.now().toString(36), from: 'staff', from_name: user.name, text: reply.trim(), timestamp: new Date().toISOString() };
      const newMsgs = [...messages, msg];
      const updates = { messages: newMsgs, message_count: newMsgs.length, last_message_at: new Date().toISOString() };
      if (!inq.first_response_at) {
        updates.first_response_at = new Date().toISOString();
        updates.response_time_minutes = Math.round((Date.now() - new Date(inq.created_at).getTime()) / 60000);
      }
      updateInquiry(inq.id, updates);
      setReply('');
    };

    const convertToCustomer = (formData) => {
      const newCust = {
        id: DB.genId('CUS'),
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        country: formData.country,
        company: formData.company,
        password: Math.random().toString(36).slice(2, 10),
        status: 'active',
        createdAt: new Date().toISOString(),
      };
      const custList = DB.get('customers') || [];
      DB.set('customers', [...custList, newCust]);
      updateInquiry(inq.id, { customer_id: newCust.id, status: 'converted' });
      setShowConvert(false);
      alert(`✅ Customer created: ${newCust.name}\nPassword: ${newCust.password}`);
    };

    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', justifyContent: 'flex-end' }} onClick={() => setDetail(null)}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
        <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 520, background: 'var(--bg1)', height: '100%', overflowY: 'auto', position: 'relative', zIndex: 1, borderLeft: '1px solid var(--border)' }}>
          {/* Header */}
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'var(--bg1)', zIndex: 2 }}>
            <button onClick={() => setDetail(null)} style={{ background: 'none', border: 'none', color: 'var(--text2)', fontSize: 13, cursor: 'pointer' }}>← Back</button>
            <button onClick={() => setDetail(null)} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 18, cursor: 'pointer' }}>✕</button>
          </div>

          <div style={{ padding: 20 }}>
            {/* Customer info */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: avatarColor(inq.customer_name), display: 'grid', placeItems: 'center', color: '#fff', fontSize: 16, fontWeight: 700 }}>
                {initials(inq.customer_name)}
              </div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text1)' }}>{inq.customer_name}</div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                  {getFlag(inq.customer_country_code)} {inq.customer_country_code || ''} · {inq.customer_phone || 'No phone'}
                </div>
              </div>
            </div>

            {/* Meta */}
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
              Channel: <span style={{ padding: '2px 6px', borderRadius: 4, background: ch.bg, color: ch.color, fontSize: 10, fontWeight: 600 }}>{ch.label}</span>
              {' · '}Priority: {inq.priority === 'hot' ? '🔴 Hot' : inq.priority === 'low' ? 'Low' : 'Normal'}
              {' · '}Created: {timeAgo(inq.created_at)} ago
            </div>

            {/* Status + Assignment */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', marginBottom: 4 }}>Status</div>
                <select value={inq.status} onChange={e => updateInquiry(inq.id, { status: e.target.value })} className="form-input" style={{ fontSize: 13 }}>
                  {COLUMNS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                  <option value="converted">Converted</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', marginBottom: 4 }}>Assigned To</div>
                <select value={inq.assigned_to || ''} onChange={e => updateInquiry(inq.id, { assigned_to: e.target.value || null, assigned_to_name: users.find(u => u.id === e.target.value)?.name || '' })} className="form-input" style={{ fontSize: 13 }}>
                  <option value="">Unassigned</option>
                  {users.filter(u => u.role === 'admin' || u.role === 'head_admin').map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            </div>

            {/* Linked unit */}
            {inq.unit_name && (
              <div style={{ padding: 14, background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 20 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Linked Unit</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text1)' }}>🚗 {inq.unit_name}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{inq.unit_sku}</div>
              </div>
            )}

            {/* Quoted amount */}
            {inq.quoted_amount && (
              <div style={{ padding: 14, background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 20 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Quote Sent</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--blue)' }}>{inq.quoted_currency || 'USD'} {Number(inq.quoted_amount).toLocaleString()}</div>
              </div>
            )}

            {/* Conversation */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12, borderTop: '1px solid var(--border)', paddingTop: 16 }}>Conversation</div>
              {messages.length === 0 && <div style={{ fontSize: 13, color: 'var(--text3)', fontStyle: 'italic' }}>No messages yet.</div>}
              {messages.map(m => (
                <div key={m.id} style={{ marginBottom: 12, padding: 12, borderRadius: 10, background: m.from === 'staff' ? 'var(--blue-bg, rgba(37,99,235,0.08))' : 'var(--bg2)', border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: m.from === 'staff' ? 'var(--blue)' : 'var(--text1)' }}>
                      {m.from === 'staff' ? (m.from_name || 'Staff') : 'Customer'}
                    </span>
                    <span style={{ fontSize: 10, color: 'var(--text3)' }}>{timeAgo(m.timestamp)} ago</span>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>{m.text}</div>
                </div>
              ))}

              {/* Reply box */}
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <input value={reply} onChange={e => setReply(e.target.value)} placeholder="Type a note/reply..." className="form-input" style={{ flex: 1, fontSize: 13 }}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }} />
                <button onClick={sendReply} className="btn btn-primary btn-sm">Send</button>
              </div>
            </div>

            {/* Actions */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Actions</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {waNum && (
                  <a href={`https://wa.me/${waNum}`} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>📱 WhatsApp</a>
                )}
                {inq.customer_email && (
                  <a href={`mailto:${inq.customer_email}?subject=${encodeURIComponent('Re: ' + inq.subject)}`} className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>📧 Email</a>
                )}
                {inq.customer_phone && (
                  <a href={`tel:${inq.customer_phone}`} className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>📞 Call</a>
                )}
                <button onClick={() => {
                  const amt = prompt('Quote amount:');
                  if (amt) updateInquiry(inq.id, { quoted_amount: parseFloat(amt), quoted_currency: 'USD', status: 'quoted' });
                }} className="btn btn-secondary btn-sm">💰 Send Quote</button>
                <button onClick={() => setShowConvert(true)} className="btn btn-secondary btn-sm">👤 Convert to Customer</button>
                <button onClick={() => {
                  const p = inq.priority === 'hot' ? 'normal' : 'hot';
                  updateInquiry(inq.id, { priority: p });
                }} className="btn btn-secondary btn-sm">{inq.priority === 'hot' ? '⭐ Unmark Hot' : '⭐ Set Hot'}</button>
              </div>
            </div>
          </div>

          {/* Convert to Customer modal */}
          {showConvert && (() => {
            const [cf, setCf] = useState({ name: inq.customer_name, phone: inq.customer_phone || '', email: inq.customer_email || '', country: inq.customer_country_code || '', company: '' });
            return (
              <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowConvert(false)}>
                <div onClick={e => e.stopPropagation()} style={{ width: 420, background: 'var(--bg1)', borderRadius: 16, border: '1px solid var(--border)', padding: 24 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: 'var(--text1)' }}>Convert to Customer</div>
                  {[['Name', 'name'], ['Phone', 'phone'], ['Email', 'email'], ['Country', 'country'], ['Company', 'company']].map(([label, key]) => (
                    <div key={key} style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>{label}</label>
                      <input value={cf[key]} onChange={e => setCf(prev => ({ ...prev, [key]: e.target.value }))} className="form-input" style={{ fontSize: 13 }} />
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                    <button onClick={() => convertToCustomer(cf)} className="btn btn-primary">Convert</button>
                    <button onClick={() => setShowConvert(false)} className="btn btn-secondary">Cancel</button>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  /* ══════════ TABLE VIEW ══════════ */
  const TableView = () => (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--border)' }}>
            {['Customer', 'Country', 'Subject', 'Channel', 'Status', 'Priority', 'Msgs', 'Age', 'Assigned'].map(h => (
              <th key={h} style={{ textAlign: 'left', padding: '10px 12px', fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).map(inq => {
            const ch = CHANNELS[inq.channel] || CHANNELS.other;
            const col = COLUMNS.find(c => c.id === inq.status);
            return (
              <tr key={inq.id} onClick={() => setDetail(inq)} style={{ cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg2)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                <td style={{ padding: '10px 12px', fontWeight: 600 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 6, background: avatarColor(inq.customer_name), display: 'grid', placeItems: 'center', color: '#fff', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{initials(inq.customer_name)}</div>
                    {inq.customer_name}
                  </div>
                </td>
                <td style={{ padding: '10px 12px' }}>{getFlag(inq.customer_country_code)} {inq.customer_country_code}</td>
                <td style={{ padding: '10px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inq.subject}</td>
                <td style={{ padding: '10px 12px' }}><span style={{ padding: '2px 6px', borderRadius: 4, background: ch.bg, color: ch.color, fontSize: 9, fontWeight: 600 }}>{ch.label}</span></td>
                <td style={{ padding: '10px 12px' }}><span style={{ color: col?.color }}>{col?.dot} {col?.label}</span></td>
                <td style={{ padding: '10px 12px' }}>{inq.priority === 'hot' ? '🔴 Hot' : inq.priority === 'low' ? 'Low' : '—'}</td>
                <td style={{ padding: '10px 12px', textAlign: 'center' }}>{inq.message_count || 1}</td>
                <td style={{ padding: '10px 12px' }}>{timeAgo(inq.created_at)}</td>
                <td style={{ padding: '10px 12px' }}>{inq.assigned_to_name || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {filtered.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>No inquiries yet.</div>}
    </div>
  );

  /* ══════════ MAIN RENDER ══════════ */
  const newCount = filtered.filter(i => i.status === 'new').length;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Sales</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text1)', marginBottom: 4 }}>Inquiries</h1>
            <p style={{ fontSize: 13, color: 'var(--text3)' }}>WhatsApp, form, and phone leads — all in one inbox.</p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* View toggle */}
            <div style={{ display: 'flex', borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden' }}>
              <button onClick={() => setView('kanban')} style={{ padding: '6px 14px', fontSize: 12, fontWeight: 500, border: 'none', cursor: 'pointer', background: view === 'kanban' ? 'var(--blue)' : 'var(--bg2)', color: view === 'kanban' ? '#fff' : 'var(--text2)' }}>Kanban</button>
              <button onClick={() => setView('table')} style={{ padding: '6px 14px', fontSize: 12, fontWeight: 500, border: 'none', cursor: 'pointer', borderLeft: '1px solid var(--border)', background: view === 'table' ? 'var(--blue)' : 'var(--bg2)', color: view === 'table' ? '#fff' : 'var(--text2)' }}>Table</button>
            </div>
            {/* Channel filter */}
            <select value={channelFilter} onChange={e => setChannelFilter(e.target.value)} className="form-input" style={{ fontSize: 12, padding: '6px 10px', width: 'auto' }}>
              <option value="">All channels</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="website_form">Website Form</option>
              <option value="phone">Phone</option>
              <option value="email">Email</option>
              <option value="walk_in">Walk-in</option>
            </select>
            {/* New inquiry */}
            <button onClick={() => setShowNew(true)} className="btn btn-primary btn-sm" style={{ whiteSpace: 'nowrap' }}>+ New inquiry</button>
          </div>
        </div>
      </div>

      {/* Content */}
      {view === 'kanban' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, alignItems: 'flex-start', minHeight: 400 }} className="inq-kanban-grid">
          {COLUMNS.map(col => {
            const colItems = filtered.filter(i => i.status === col.id).sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
            return (
              <div key={col.id}
                onDragOver={e => e.preventDefault()}
                onDrop={e => handleDrop(col.id, e)}
                style={{ background: 'var(--bg2)', borderRadius: 12, padding: 12, minHeight: 200, border: dragId ? '2px dashed var(--border)' : '2px solid transparent', transition: 'border 0.15s' }}>
                {/* Column header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, padding: '0 4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>{col.dot}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text1)', textTransform: 'uppercase', letterSpacing: '0.02em' }}>{col.label}</span>
                    <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 500 }}>{colItems.length}</span>
                  </div>
                  <button onClick={() => setShowNew(true)} style={{ width: 24, height: 24, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg1)', color: 'var(--text3)', fontSize: 14, cursor: 'pointer', display: 'grid', placeItems: 'center' }}>+</button>
                </div>
                {/* Cards */}
                {colItems.map(inq => <InquiryCard key={inq.id} inq={inq} />)}
                {colItems.length === 0 && <div style={{ textAlign: 'center', padding: '24px 0', fontSize: 12, color: 'var(--text3)' }}>No inquiries</div>}
              </div>
            );
          })}
        </div>
      ) : (
        <TableView />
      )}

      {/* Detail panel */}
      {detail && <DetailPanel />}

      {/* New inquiry modal */}
      {showNew && <NewInquiryModal />}

      <style>{`
        @media (max-width: 968px) { .inq-kanban-grid { grid-template-columns: 1fr 1fr !important; } }
        @media (max-width: 600px) { .inq-kanban-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
}

export default Inquiries;
